import cn from 'classnames';
import React, { FC, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { UserResource } from '@.api/methods';
import { Button, Input, Select } from '@.ui';
import { Board } from '@/Components/Partials';
import { getUserRoleDictionaryByRoleEnum } from '@/Lib';
import { StatisticObjectEnum } from '@/Services/StatisticPermissionService';
import { UserRoleEnum } from '@/Types/api/User';

import { AccessObjectSelect } from './AccessObjectSelect';
import { StatisticObjectPermissionsSelect } from './StatisticObjectPermissionsSelect';
import S from './UserForm.module.scss';
import { useUserFormAction } from './useUserFormAction';

type UserFormProps = {
    isEdit?: boolean;
    userId?: number;
    isFormDisabled?: boolean;
    title?: ReactNode;
    initialResponseUserData?: UserResource;
    createSaveButton?: () => ReactNode;
    createCancelButton?: () => ReactNode;
};

export const UserForm: FC<UserFormProps> = ({
    isEdit = false,
    userId,
    isFormDisabled = false,
    title,
    initialResponseUserData,
    createCancelButton,
    createSaveButton,
}) => {
    const navigate = useNavigate();

    const { field, form, fetchFormAction, dictionaries, storeGroupsForServiceProvider } = useUserFormAction(isEdit, userId, initialResponseUserData);

    const serviceProviderRole = getUserRoleDictionaryByRoleEnum(dictionaries?.roles || [], UserRoleEnum.SERVICE_PROVIDER);
    const defaultUserRole = getUserRoleDictionaryByRoleEnum(dictionaries?.roles || [], UserRoleEnum.DEFAULT_USER);
    const serviceEngineerRole = getUserRoleDictionaryByRoleEnum(dictionaries?.roles || [], UserRoleEnum.SERVICE_ENGINEER);
    const productManagerRole = getUserRoleDictionaryByRoleEnum(dictionaries?.roles || [], UserRoleEnum.PRODUCT_MANAGER);

    const isDisabled = useMemo<boolean>(() => {
        const statisticObjectType = form.state.statistic_object_type?.value;

        if (statisticObjectType === StatisticObjectEnum.COMPANY) {
            return !form.state.company_id?.value;
        } else if (statisticObjectType === StatisticObjectEnum.STORE_GROUP) {
            return !form.state.statistic_object_store_group_id?.value;
        } else if (statisticObjectType === StatisticObjectEnum.STORE) {
            return !form.state.statistic_object_store_id?.value;
        }

        return isFormDisabled;
    }, [form.state, isFormDisabled]);

    const isSelectedServiceProviderRole = form.state.role_id?.value === serviceProviderRole?.id;

    const isSelectedRoleForAccessObject =
        form.state.role_id?.value === defaultUserRole?.id ||
        form.state.role_id?.value === serviceEngineerRole?.id ||
        form.state.role_id?.value === productManagerRole?.id;

    const isShowSpClusterList = isSelectedServiceProviderRole;

    const back = () => navigate(-1);

    return (
        <Board.Item>
            <h1>{title}</h1>
            <div className={S.usersAction__form}>
                <Input {...field('name').register()} text="Name" required disabled={isFormDisabled} />
                <Input {...field('username').register()} text="Username" required disabled={isFormDisabled} />
                <Input {...field('email').register()} text="Email" type="email" required disabled={isFormDisabled} />
                <Input {...field('password').register()} autoComplete="new-password" text="New password" type="password" required disabled={isFormDisabled} />
                <Input
                    disabled={isFormDisabled}
                    {...field('repeat_password').register()}
                    autoComplete="new-password"
                    text="Confirm password"
                    type="password"
                    required
                />

                {!isSelectedServiceProviderRole && (
                    <Select
                        {...field('company_id').register<number>()}
                        label="Company"
                        placeholder="Company"
                        theme="white"
                        required={true}
                        autoClose
                        disabled={isFormDisabled}
                    >
                        {dictionaries?.companies?.map((company) => (
                            <Select.Option id={company.id} key={`company_${company.id}`}>
                                {company.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}

                {isSelectedRoleForAccessObject && <AccessObjectSelect form={form} field={field} dictionaries={dictionaries} isFormDisabled={isFormDisabled} />}

                {!isSelectedServiceProviderRole && <StatisticObjectPermissionsSelect form={form} field={field} dictionaries={dictionaries} />}

                <Select
                    disabled={isFormDisabled}
                    {...field('role_id').register<number>()}
                    label="Role"
                    placeholder="Role"
                    theme="white"
                    required={true}
                    autoClose
                >
                    {dictionaries?.roles?.map((role) => (
                        <Select.Option key={role.id} id={role.id}>
                            {role.name}
                        </Select.Option>
                    ))}
                </Select>

                {isShowSpClusterList && (
                    <div className={S.storeGroup}>
                        <Select {...field('sp_cluster_id').register<number>()} placeholder="Store cluster" localSearch disabled={isFormDisabled}>
                            {storeGroupsForServiceProvider.map((cluster) => (
                                <Select.Option key={cluster.value} id={Number(cluster.value)}>
                                    {cluster.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                )}

                {/* <div className={cn(S.storeGroup, isShowSpClusterList && isHiddenStoreGroup && S.storeGroup_hide)}>
                    <Select
                        {...field('store_groups_ids').register<number[]>()}
                        placeholder="Store groups"
                        localSearch
                        multiple
                        disabled={isFormDisabled || Boolean(form.state.is_company_admin?.value)}
                    >
                        {groups.map((group) => (
                            <Select.Option key={group.id} id={group.id}>
                                {group.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div> */}

                {isEdit && (
                    <Select
                        {...field('status').register<number>()}
                        label="Status"
                        placeholder="Status"
                        autoClose
                        theme="white"
                        required
                        disabled={isFormDisabled}
                    >
                        {dictionaries?.user_statuses?.map((status) => (
                            <Select.Option key={status.id} id={status.id}>
                                {status.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}

                <div className={S.buttons}>
                    {createCancelButton ? (
                        createCancelButton()
                    ) : (
                        <Button color="transparent" onClick={back}>
                            Cancel
                        </Button>
                    )}

                    {createSaveButton ? (
                        createSaveButton()
                    ) : (
                        <Button color="brown" onClick={fetchFormAction} disabled={isDisabled || isFormDisabled}>
                            {isEdit ? 'Save' : 'Add'}
                        </Button>
                    )}
                </div>
            </div>
        </Board.Item>
    );
};
